/**
 * Error reporter — i crash del frontend finiscono nell'error tracking
 * interno (POST /errors/report, dedup lato server per fingerprint).
 *
 * Best-effort totale: mai un errore del reporter deve disturbare
 * l'utente. Dedup di sessione per non riempire la rete di duplicati.
 */
import { apiReportError } from './api';
import { authStorage } from './auth';

const seen = new Set<string>();

/**
 * Rumore noto del browser da NON riportare: non sono bug, non rompono nulla,
 * li generano le librerie UI (ResizeObserver di select/dropdown/animazioni).
 * Riportarli spamma l'admin con falsi allarmi. Match case-insensitive.
 */
const IGNORED_PATTERNS = [
  'resizeobserver loop completed with undelivered notifications',
  'resizeobserver loop limit exceeded',
  // Errori di rete/script cross-origin opachi (nessuno stack utile).
  'script error',
  'load failed',
  'networkerror when attempting to fetch resource',
];

function isIgnorable(message: string): boolean {
  const m = message.toLowerCase();
  return IGNORED_PATTERNS.some((p) => m.includes(p));
}

function report(message: string, stack?: string | null) {
  try {
    // Solo se autenticati (l'endpoint richiede il token)
    if (!authStorage.getToken()) return;
    // Filtra il rumore noto del browser prima di tutto il resto.
    if (isIgnorable(message)) return;
    const key = message.slice(0, 120);
    if (seen.has(key)) return;       // una volta per sessione
    seen.add(key);
    void apiReportError({
      message: message.slice(0, 4000),
      stack: stack ? stack.slice(0, 20000) : null,
      url: window.location.pathname,
    }).catch(() => undefined);
  } catch {
    /* mai propagare */
  }
}

export function installErrorReporter(): void {
  window.addEventListener('error', (event) => {
    report(
      event.message || 'Errore sconosciuto',
      event.error instanceof Error ? event.error.stack : null,
    );
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    report(
      reason instanceof Error
        ? `Unhandled rejection: ${reason.message}`
        : `Unhandled rejection: ${String(reason).slice(0, 200)}`,
      reason instanceof Error ? reason.stack : null,
    );
  });
}
